import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import { StarCeremonyEffect } from "./StarCeremonyEffect.js";
import { fitWorkshopCamera } from "./fitWorkshopCamera.js";
import { COSMETICS } from "@zend/content";
import { applyCosmeticPaint } from "../assets/cosmeticPaint.js";

/** The fabrication floor is shared by the collection turntable and the full-screen reveal. */
export function createWorkshopStage(accent: string): {
  group: THREE.Group;
  accent: THREE.MeshStandardMaterial;
} {
  const group = new THREE.Group();
  const plaster = new THREE.MeshStandardMaterial({ color: 0x596361, roughness: 0.72, metalness: 0.24 });
  const floor = new THREE.MeshStandardMaterial({ color: 0x28383b, roughness: 0.92 });
  const trim = new THREE.MeshStandardMaterial({ color: 0x202c34, roughness: 0.52, metalness: 0.62 });
  const brass = new THREE.MeshStandardMaterial({ color: 0xb48c50, roughness: 0.5, metalness: 0.5 });
  const marker = new THREE.MeshStandardMaterial({
    color: accent,
    emissive: accent,
    emissiveIntensity: 0.24,
    roughness: 0.45,
    metalness: 0.15,
  });
  const lamp = new THREE.MeshStandardMaterial({
    color: 0xffebc1,
    emissive: 0xffebc1,
    emissiveIntensity: 1.2,
    roughness: 0.4,
  });
  const mesh = (
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    x: number,
    y: number,
    z: number,
  ) => {
    const item = new THREE.Mesh(geometry, material);
    item.position.set(x, y, z);
    item.receiveShadow = true;
    group.add(item);
    return item;
  };
  mesh(new THREE.BoxGeometry(18, 0.12, 18), floor, 0, -0.38, 0);
  mesh(new THREE.CylinderGeometry(1.4, 1.5, 0.24, 48), plaster, 0, -0.14, 0);
  mesh(new THREE.CylinderGeometry(1.32, 1.32, 0.035, 48), trim, 0, -0.003, 0);
  const deck = mesh(new THREE.CircleGeometry(1.24, 48), plaster, 0, 0.018, 0);
  deck.rotation.x = -Math.PI / 2;
  const ring = mesh(new THREE.RingGeometry(1.27, 1.3, 64), marker, 0, 0.02, 0);
  ring.rotation.x = -Math.PI / 2;
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const index = mesh(
      new THREE.BoxGeometry(0.09, 0.012, 0.19),
      brass,
      Math.sin(a) * 1.13,
      0.028,
      Math.cos(a) * 1.13,
    );
    index.rotation.y = a;
  }
  // Bay architecture gives the metal models a scale reference without crossing their silhouette.
  mesh(new THREE.BoxGeometry(9, 3.5, 0.15), plaster, 0, 1.38, -3.0);
  mesh(new THREE.BoxGeometry(9, 0.14, 0.22), trim, 0, 0.28, -2.88);
  const door = new THREE.MeshStandardMaterial({ color: 0x35494c, roughness: 0.7, metalness: 0.38 });
  mesh(new THREE.BoxGeometry(2.8, 2.7, 0.1), trim, 0, 1.05, -2.86);
  mesh(new THREE.BoxGeometry(2.54, 2.48, 0.04), door, 0, 1.05, -2.79);
  for (let i = 0; i < 6; i++)
    mesh(new THREE.BoxGeometry(2.54, 0.035, 0.045), trim, 0, 0.04 + i * 0.42, -2.755);
  mesh(new THREE.BoxGeometry(0.035, 2.48, 0.045), brass, 0, 1.05, -2.75);
  for (const side of [-1, 1]) {
    mesh(new THREE.BoxGeometry(0.21, 3.8, 0.25), trim, side * 2.35, 1.5, -2.7);
    mesh(new THREE.BoxGeometry(1.65, 1.2, 0.08), trim, side * 2.35, 1.63, -2.89);
    const glass = new THREE.MeshStandardMaterial({
      color: 0x9bc3c5,
      emissive: 0xc5e1dc,
      emissiveIntensity: 0.18,
      metalness: 0.15,
      roughness: 0.25,
    });
    mesh(new THREE.BoxGeometry(1.45, 1.02, 0.035), glass, side * 2.35, 1.63, -2.83);
    mesh(new THREE.BoxGeometry(1.48, 0.035, 0.055), plaster, side * 2.35, 1.63, -2.79);
    mesh(new THREE.BoxGeometry(0.035, 1.06, 0.055), plaster, side * 2.35, 1.63, -2.79);
    mesh(new THREE.BoxGeometry(0.65, 0.62, 0.36), trim, side * 2.13, -0.01, -1.58);
    mesh(new THREE.BoxGeometry(0.7, 0.06, 0.41), brass, side * 2.13, 0.32, -1.58);
    mesh(new THREE.BoxGeometry(0.48, 0.025, 0.025), marker, side * 2.13, 0.11, -1.39);
    mesh(new THREE.BoxGeometry(0.11, 0.025, 2.2), plaster, side * 1.76, -0.31, 0.35);
    // Service cabinets, rails and floor fasteners give the bay a working scale.
    for (let i = 0; i < 3; i++) {
      mesh(new THREE.BoxGeometry(0.56, 0.13, 0.04), plaster, side * 2.13, -0.2 + i * 0.16, -1.38);
      mesh(new THREE.BoxGeometry(0.17, 0.025, 0.055), brass, side * 2.13, -0.15 + i * 0.16, -1.35);
    }
    for (let i = 0; i < 6; i++) {
      const hazard = mesh(new THREE.BoxGeometry(0.07, 0.014, 0.23), brass, side * 1.76, -0.291, -0.4 + i * 0.3);
      hazard.rotation.y = -0.55;
    }
    for (let i = 0; i < 5; i++)
      mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.014, 6), brass, side * 1.18, 0.039, -0.52 + i * 0.25);
  }
  mesh(new THREE.BoxGeometry(0.48, 0.5, 0.48), trim, -2.06, -0.05, -0.15);
  const diagnostic = mesh(new THREE.BoxGeometry(0.43, 0.28, 0.06), plaster, -2.06, 0.3, -0.15);
  diagnostic.rotation.x = -0.35;
  const display = mesh(new THREE.PlaneGeometry(0.34, 0.16), marker, -2.06, 0.31, -0.108);
  display.rotation.x = -0.35;
  // Overhead lifting trolley stays behind the subject and above its silhouette.
  mesh(new THREE.BoxGeometry(4.7, 0.14, 0.34), brass, 0, 2.95, -2.35);
  mesh(new THREE.BoxGeometry(0.42, 0.24, 0.44), trim, -1.44, 2.78, -2.35);
  mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.55, 8), brass, -1.44, 2.4, -2.35);
  const hook = mesh(new THREE.TorusGeometry(0.09, 0.018, 6, 12, Math.PI * 1.55), brass, -1.44, 2.08, -2.35);
  hook.rotation.z = 0.2;
  mesh(new THREE.BoxGeometry(5.0, 0.22, 0.28), trim, 0, 3.3, -2.7);
  mesh(new THREE.BoxGeometry(3.9, 0.065, 0.14), lamp, 0, 3.17, -2.59);
  return { group, accent: marker };
}

/** Metal needs broad reflected light as well as the sun/key light to show its paint and machined edges. */
export function createWorkshopEnvironment(renderer: THREE.WebGLRenderer): THREE.WebGLRenderTarget {
  const room = new RoomEnvironment();
  const generator = new THREE.PMREMGenerator(renderer);
  const environment = generator.fromScene(room, 0.04);
  room.dispose();
  generator.dispose();
  return environment;
}

export function disposeWorkshopStage(group: THREE.Group): void {
  const materials = new Set<THREE.Material>();
  group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.geometry.dispose();
    for (const material of Array.isArray(object.material) ? object.material : [object.material])
      materials.add(material);
  });
  for (const material of materials) material.dispose();
}

/**
 * A small self-contained turntable renderer for one unit model (detail sheet, reveal, collection).
 * Own WebGL context, no post-processing; disposes cleanly on unmount (R29 collection, R27 reveal).
 */
export class UnitViewer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly rim: THREE.DirectionalLight;
  private readonly stage: ReturnType<typeof createWorkshopStage>;
  private readonly environment: THREE.WebGLRenderTarget;
  private readonly modelMount = new THREE.Group();
  private modelHeight = 1.8;
  private instance: ModelInstance | null = null;
  private raf = 0;
  private last = 0;
  private token = 0;
  private spin = 0.36;
  private disposed = false;
  private active = true;
  private reducedMotion = false;
  private ceremony: StarCeremonyEffect | null = null;
  private onCeremonyComplete: (() => void) | null = null;
  private skin: string | null = null;
  private assetId = "";
  private restorePaint: (() => void) | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    options: { accent?: string; clip?: string; reducedMotion?: boolean } = {},
  ) {
    this.reducedMotion = options.reducedMotion ?? false;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: "low-power",
    });
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.scene.background = new THREE.Color(0x26343c);
    this.scene.fog = new THREE.Fog(0x26343c, 8, 18);
    this.environment = createWorkshopEnvironment(this.renderer);
    this.scene.environment = this.environment.texture;
    this.scene.environmentIntensity = 0.7;
    this.camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    this.scene.add(new THREE.HemisphereLight(0xd8e9eb, 0xb0a080, 1.2));
    const key = new THREE.DirectionalLight(0xffedcf, 1.8);
    key.position.set(-3, 5, 4);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.left = -3;
    key.shadow.camera.right = 3;
    key.shadow.camera.top = 4;
    key.shadow.camera.bottom = -2;
    key.shadow.normalBias = 0.035;
    this.scene.add(key);
    this.rim = new THREE.DirectionalLight(new THREE.Color(options.accent ?? "#599c9d"), 0.75);
    this.rim.position.set(-3, 2, -3);
    this.scene.add(this.rim);
    this.stage = createWorkshopStage(options.accent ?? "#599c9d");
    this.scene.add(this.stage.group, this.modelMount);
    this.resize();
    this.raf = requestAnimationFrame(this.loop);
  }

  setAccent(color: string): void {
    this.rim.color.set(color);
    this.stage.accent.color.set(color);
    this.stage.accent.emissive.set(color);
  }

  setSkin(id: string | null): void {
    this.skin = id;
    this.paint();
  }

  private paint(): void {
    this.restorePaint?.();
    this.restorePaint = null;
    const cosmetic = COSMETICS.find((candidate) => candidate.id === this.skin && candidate.target === this.assetId);
    if (this.instance && cosmetic)
      this.restorePaint = applyCosmeticPaint(this.instance.object, { ...cosmetic, colour: cosmetic.colour ?? null });
  }

  /** Swaps the shown model; the previous one is removed once the new one loaded. */
  async show(id: string, variant?: number | string, clip = "idle"): Promise<void> {
    const token = ++this.token;
    const model = await loadModel(id, variant);
    if (this.disposed || token !== this.token) return;
    this.restorePaint?.();
    this.restorePaint = null;
    if (this.instance) {
      this.instance.mixer.stopAllAction();
      this.instance.mixer.uncacheRoot(this.instance.object);
      this.modelMount.remove(this.instance.object);
    }
    const inst = instantiate(model);
    const box = new THREE.Box3().setFromObject(inst.object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const scale = 2.0 / Math.max(size.x, size.y, size.z, 0.01);
    inst.object.scale.setScalar(scale);
    inst.object.position.set(-center.x * scale, 0.035 - box.min.y * scale, -center.z * scale);
    this.modelHeight = size.y * scale;
    this.modelMount.rotation.y = -0.35;
    inst.play(clip);
    inst.mixer.update(0);
    this.modelMount.add(inst.object);
    this.instance = inst;
    this.assetId = id;
    this.paint();
    this.resize();
  }

  play(clip: string): void {
    this.instance?.play(clip, { loop: clip === "idle" || clip === "walk" });
  }

  setSpin(radPerSec: number): void {
    this.spin = radPerSec;
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
    if (reduced) this.finishCeremony();
  }

  celebrateStars(stars: number, onComplete: () => void): void {
    if (this.disposed || !this.instance) return;
    if (this.ceremony) {
      this.scene.remove(this.ceremony.group);
      this.ceremony.dispose();
    }
    this.spin = 0;
    this.ceremony = new StarCeremonyEffect(stars, this.modelHeight);
    this.scene.add(this.ceremony.group);
    this.onCeremonyComplete = onComplete;
    this.resize();
    if (this.reducedMotion) this.finishCeremony();
  }

  finishCeremony(): void {
    if (!this.ceremony) return;
    this.ceremony.finish();
    const complete = this.onCeremonyComplete;
    this.onCeremonyComplete = null;
    complete?.();
  }

  setActive(active: boolean): void {
    this.active = active;
    this.last = performance.now();
  }

  resize(): void {
    const w = Math.max(1, this.canvas.clientWidth);
    const h = Math.max(1, this.canvas.clientHeight);
    this.camera.aspect = w / h;
    const halfFov = THREE.MathUtils.degToRad(this.camera.fov / 2);
    const subjectHeight = this.ceremony?.height ?? this.modelHeight;
    const targetHeight = Math.max(0.52, subjectHeight * 0.5);
    const distance =
      Math.max(
        1.47 / (Math.tan(halfFov) * this.camera.aspect),
        (subjectHeight + 0.5) / (2 * Math.tan(halfFov)),
      ) * 1.13;
    this.camera.position.set(0, targetHeight + distance * 0.27, distance);
    this.camera.lookAt(0, targetHeight, 0);
    this.camera.updateProjectionMatrix();
    if (this.ceremony && this.instance) {
      const bounds = new THREE.Box3().setFromObject(this.modelMount);
      // Include the final crown and its travel envelope before the animation starts.
      bounds.expandByPoint(new THREE.Vector3(-1.1, 0.035, 0.7));
      bounds.expandByPoint(new THREE.Vector3(1.1, this.ceremony.height, 0));
      fitWorkshopCamera(this.camera, bounds, targetHeight);
    }
    this.renderer.setSize(w, h, false);
  }

  private readonly loop = (now: number) => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    if (!this.active) return;
    if (this.instance && !this.reducedMotion) {
      this.instance.mixer.update(dt);
      this.modelMount.rotation.y += this.spin * dt;
    }
    if (this.ceremony && !this.ceremony.done && this.ceremony.update(dt)) this.finishCeremony();
    this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    ++this.token;
    cancelAnimationFrame(this.raf);
    this.restorePaint?.();
    this.restorePaint = null;
    this.instance?.mixer.stopAllAction();
    if (this.instance) this.instance.mixer.uncacheRoot(this.instance.object);
    this.instance = null;
    this.modelMount.clear();
    this.onCeremonyComplete = null;
    this.ceremony?.dispose();
    this.ceremony = null;
    // Imported model geometry/materials belong to the loader cache and other active viewers.
    disposeWorkshopStage(this.stage.group);
    this.environment.dispose();
    this.scene.traverse((object) => {
      if (object instanceof THREE.DirectionalLight) object.shadow.dispose();
    });
    this.scene.clear();
    this.renderer.dispose();
    // StrictMode disposes and recreates effects on the same connected canvas. Losing that
    // context immediately would also break its replacement renderer; detached canvases can retire it.
    queueMicrotask(() => {
      if (!this.canvas.isConnected) this.renderer.forceContextLoss();
    });
  }
}
